import 'hammerjs';
import * as Sentry from '@sentry/angular';
import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  inject,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';
import { environment } from './environments/environment';
import { bootstrapApplication, HammerModule } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import {
  HttpClient,
  HttpXhrBackend,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { APP_ROUTES } from './app/app-routes';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { provideNgxStripe } from 'ngx-stripe';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  AppConfiguration,
  ENVIRONMENT_TOKEN,
} from './app/core/services/configuration/app.configuration';
import { NavigationService } from './app/core/services/navigation/navigation.service';
import { STRETCHITLIVE_PIXEl_URL } from './app/core/tokens/stretchitlive-pixel-url.token';
import { SIZES_TOKEN } from './app/core/tokens/sizes.token';
import { AppSizes } from './config/sizes';
import { API_NEW_BASE_URL, PROGRAMS_NEW_BASE_URL } from './app/core/tokens/new-base-url.token';
import { FEEDBACK_URL } from './app/core/tokens/feedback-url.token';
import { APP_HTTP_INTERCEPTORS } from './app/core/interceptors/app-http-interceptors';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { LANG_STORAGE_KEY } from './app/core/services/locale/locale.service';
import { initializeLocaleFactory, resolveLocaleId } from '@app-types/Locale';
import { GeoStore } from '@stores/geo.store';
import { DatePipe } from '@angular/common';
import { StCurrencyPipe } from './app/core/pipes/st-currency/st-currency.pipe';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { PushNotificationService } from './app/core/services/push-notifications/push-notification.service';
import {
  extractAngularSentryError,
  normalizeSentryHttpEvent,
} from '@http-helpers/sentry-http-error';
import { ResilientTranslateLoader } from '@http-helpers/resilient-translate-loader';
import { createChunkNavigationErrorHandler } from '@navigator-helpers/chunk-load-recovery';
import { GlobalLoadingIndicatorService } from './app/services/global-loading-indicator/global-loading-indicator.service';
import {
  NETWORK_STATUS_PROBE_URL,
  NetworkStatusService,
} from './app/core/network/network-status.service';
import { DialogService } from '@ui-components/dialog/services/dialog.service';
import { ChunkLoadErrorModalComponent } from '@modals/chunk-load-error-modal/chunk-load-error-modal.component';
import { AuthRefreshNavigationRecoveryService } from './app/core/interceptors/auth-refresh/auth-refresh-navigation-recovery.service';
import { ThemeService } from './app/core/services/theme/theme.service';

if (environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.name,
    release: `stretchit-webapp@${environment.version}`,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0,
    tracePropagationTargets: [/^https:\/\/api[-\w.]*\.stretchit(app|live)/],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend: normalizeSentryHttpEvent,
  });
}

if (environment.production) {
  enableProdMode();
}

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(objectSupport);
dayjs.extend(duration);

declare let fbq: any;

function initializeNavigationFactory(navigation: NavigationService): () => void {
  return () => navigation.init();
}

function initializeFacebookPixelFactory(config: AppConfiguration): () => void {
  return () => {
    const chargebeeQuizzes = ['quiz93', 'quiz94'].find(quiz => window.location.href.includes(quiz));

    let id: string;

    if (chargebeeQuizzes) {
      id = config.pixels.fbPixelIDChargebee;
    } else {
      id = config.pixels.fbPixelIdStripe;
    }

    fbq('init', id);
  };
}

// declare let ga: any;
// function initializeGoogleAnalyticsFactory(config: AppConfiguration): () => void {
//   return () => {
//     ga('create', config.googleAnalytics, 'auto');
//   };
// }

export function HttpLoaderFactory(
  globalLoader: GlobalLoadingIndicatorService,
  network: NetworkStatusService,
  dialog: DialogService
) {
  return new ResilientTranslateLoader(
    new HttpClient(
      new HttpXhrBackend({
        build: () => new XMLHttpRequest(),
      })
    ),
    {
      onLoadStart: () => globalLoader.showLoading(),
      onLoadEnd: () => globalLoader.hideLoading(),
      onRetryExhausted: createResourceLoadErrorDialogOpener(dialog),
      network,
    }
  );
}

function createResourceLoadErrorDialogOpener(dialog: DialogService): () => void {
  return () => {
    dialog.open(ChunkLoadErrorModalComponent).then();
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        extractor: extractAngularSentryError,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideAnimations(),
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withRouterConfig({ resolveNavigationPromiseOnError: true }),
      withNavigationErrorHandler(
        (() => {
          const handleChunkNavigationError = createChunkNavigationErrorHandler({
            version: environment.version,
            getRouter: () => inject(Router),
            getNetwork: () => inject(NetworkStatusService),
            getLoadingIndicator: () => inject(GlobalLoadingIndicatorService),
            getOpenChunkLoadErrorDialog: () => {
              const dialog = inject(DialogService);

              return createResourceLoadErrorDialogOpener(dialog);
            },
          });

          return event => {
            handleChunkNavigationError(event);
            inject(AuthRefreshNavigationRecoveryService).handle(event);
          };
        })()
      )
    ),
    {
      provide: LOCALE_ID,
      useFactory: (geoStore: { country: () => string }) =>
        resolveLocaleId({
          queryLang: new URLSearchParams(window.location.search).get('lang'),
          storedLang: localStorage.getItem(LANG_STORAGE_KEY),
          browserLang: navigator.language,
          country: geoStore.country(),
        }),
      deps: [GeoStore],
    },
    provideHttpClient(withInterceptors([...APP_HTTP_INTERCEPTORS])),
    {
      provide: NETWORK_STATUS_PROBE_URL,
      useValue: '/assets/network-probe.txt',
    },
    provideNgxStripe(environment.stripeKey),

    provideTranslateService({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [GlobalLoadingIndicatorService, NetworkStatusService, DialogService],
      },
    }),

    importProvidersFrom(HammerModule),

    {
      provide: ENVIRONMENT_TOKEN,
      useFactory: () => environment,
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initializeGoogleAnalyticsFactory,
    //   multi: true,
    //   deps: [AppConfiguration],
    // },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLocaleFactory,
      multi: true,
      deps: [LOCALE_ID],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFacebookPixelFactory,
      multi: true,
      deps: [AppConfiguration],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeNavigationFactory,
      deps: [NavigationService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [ThemeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (geoStore: { waitForLoading: () => Promise<any> }) => async () =>
        await geoStore.waitForLoading(),
      deps: [GeoStore],
      multi: true,
    },
    {
      provide: STRETCHITLIVE_PIXEl_URL,
      useFactory: (config: AppConfiguration) => config.stretchitivePixelURL,
      deps: [AppConfiguration],
    },
    {
      provide: SIZES_TOKEN,
      useValue: new AppSizes(),
    },
    {
      provide: PROGRAMS_NEW_BASE_URL,
      useFactory: (config: AppConfiguration) => config.programsNewUrl,
      deps: [AppConfiguration],
    },
    {
      provide: API_NEW_BASE_URL,
      useFactory: (config: AppConfiguration) => config.apiNewBaseUrl,
      deps: [AppConfiguration],
    },
    {
      provide: FEEDBACK_URL,
      useFactory: (config: AppConfiguration) => config.feedbackURL,
      deps: [AppConfiguration],
    },
    ...(environment.pushNotificationsEnabled
      ? [
          provideFirebaseApp(() => initializeApp(environment.firebase)),
          {
            provide: APP_INITIALIZER,
            useFactory: (pushService: PushNotificationService) => () => {
              pushService.warmup().catch();
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                pushService.listenToForegroundMessages();
              }
            },
            deps: [PushNotificationService],
            multi: true,
          },
        ]
      : []),
    DatePipe,
    StCurrencyPipe,
  ],
}).catch(err => console.log(err));
